/**
 * React Hooks for Resource Data
 */

import { useCallback, useEffect, useState } from 'react';
import { resourceRepository } from '../repositories/ResourceRepository';
import {
    EntityFilterOptions,
    EntitySortOptions,
    Resource
} from '../types/WorkflowEntities';

// Hook for fetching all resources
export function useResources(options?: {
    filter?: EntityFilterOptions;
    sort?: EntitySortOptions;
    autoRefresh?: boolean;
    refreshInterval?: number;
}) {
    const [resources, setResources] = useState<Resource[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    const fetchResources = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const data = await resourceRepository.findAll({
                filter: options?.filter as any,
                sort: options?.sort as any
            });
            setResources(data);
        } catch (err) {
            setError(err as Error);
            setResources([]);
        } finally {
            setLoading(false);
        }
    }, [options?.filter, options?.sort]);

    useEffect(() => {
        fetchResources();

        if (options?.autoRefresh && options?.refreshInterval) {
            const interval = setInterval(fetchResources, options.refreshInterval);
            return () => clearInterval(interval);
        }
        return undefined;
    }, [fetchResources, options?.autoRefresh, options?.refreshInterval]);

    return {
        resources,
        loading,
        error,
        refetch: fetchResources
    };
}

// Hook for fetching a single resource
export function useResource(resourceId: string | null) {
    const [resource, setResource] = useState<Resource | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const fetchResource = useCallback(async () => {
        if (!resourceId) {
            setResource(null);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const data = await resourceRepository.findById(resourceId);
            setResource(data);
        } catch (err) {
            setError(err as Error);
            setResource(null);
        } finally {
            setLoading(false);
        }
    }, [resourceId]);

    useEffect(() => {
        fetchResource();
    }, [fetchResource]);

    return {
        resource,
        loading,
        error,
        refetch: fetchResource
    };
}

// Hook for resource CRUD operations
export function useResourceMutations() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const createResource = useCallback(async (data: Partial<Resource>) => {
        setLoading(true);
        setError(null);

        try {
            const result = await resourceRepository.create(data);
            return result;
        } catch (err) {
            setError(err as Error);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const updateResource = useCallback(async (id: string, data: Partial<Resource>) => {
        setLoading(true);
        setError(null);

        try {
            const result = await resourceRepository.update(id, data);
            return result;
        } catch (err) {
            setError(err as Error);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const deleteResource = useCallback(async (id: string) => {
        setLoading(true);
        setError(null);

        try {
            const result = await resourceRepository.delete(id);
            return result;
        } catch (err) {
            setError(err as Error);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const allocateResource = useCallback(async (resourceId: string, amount: number) => {
        setLoading(true);
        setError(null);

        try {
            const result = await resourceRepository.allocate(resourceId, amount);
            return result;
        } catch (err) {
            setError(err as Error);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const releaseResource = useCallback(async (resourceId: string, amount: number) => {
        setLoading(true);
        setError(null);

        try {
            const result = await resourceRepository.release(resourceId, amount);
            return result;
        } catch (err) {
            setError(err as Error);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    return {
        createResource,
        updateResource,
        deleteResource,
        allocateResource,
        releaseResource,
        loading,
        error
    };
}

// Hook for resource search
export function useResourceSearch(query: string, debounceMs: number = 300) {
    const [results, setResults] = useState<Resource[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        let cleanup: (() => void) | undefined;
        if (!query || query.trim() === '') {
            setResults([]);
        } else {
            const timer = setTimeout(async () => {
                setLoading(true);
                setError(null);

                try {
                    const data = await resourceRepository.search(query);
                    setResults(data);
                } catch (err) {
                    setError(err as Error);
                    setResults([]);
                } finally {
                    setLoading(false);
                }
            }, debounceMs);
            cleanup = () => clearTimeout(timer);
        }
        return cleanup;
    }, [query, debounceMs]);

    return {
        results,
        loading,
        error
    };
}

// Hook for resources by type
export function useResourcesByType(type: string) {
    const [resources, setResources] = useState<Resource[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        const fetchResources = async () => {
            setLoading(true);
            setError(null);

            try {
                const data = await resourceRepository.findByType(type);
                setResources(data);
            } catch (err) {
                setError(err as Error);
                setResources([]);
            } finally {
                setLoading(false);
            }
        };

        fetchResources();
    }, [type]);

    return {
        resources,
        loading,
        error
    };
}

// Hook for available resources
export function useAvailableResources() {
    const [resources, setResources] = useState<Resource[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        const fetchAvailable = async () => {
            setLoading(true);
            setError(null);

            try {
                const data = await resourceRepository.findAvailable();
                setResources(data);
            } catch (err) {
                setError(err as Error);
                setResources([]);
            } finally {
                setLoading(false);
            }
        };

        fetchAvailable();
    }, []);

    return {
        resources,
        loading,
        error
    };
}

// Hook for resource utilization
export function useResourceUtilization(resourceId?: string) {
    const [utilization, setUtilization] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        const fetchUtilization = async () => {
            setLoading(true);
            setError(null);

            try {
                const data = await resourceRepository.getUtilization(resourceId);
                setUtilization(data);
            } catch (err) {
                setError(err as Error);
                setUtilization(null);
            } finally {
                setLoading(false);
            }
        };

        fetchUtilization();
    }, [resourceId]);

    return {
        utilization,
        loading,
        error
    };
}

export default {
    useResources,
    useResource,
    useResourceMutations,
    useResourceSearch,
    useResourcesByType,
    useAvailableResources,
    useResourceUtilization
};